import { useCallback, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { getApiErrorMessage } from '@/utils/apiValidation';
import { useAuth } from '@/context';
import { useSocket } from '@/context/SocketContext';
import { notificationService } from '@/services/notificationService';
import { QUERY_KEYS } from '@/constants';

export function useNotifications(params = {}) {
  return useQuery({
    queryKey: [...QUERY_KEYS.NOTIFICATIONS, params],
    queryFn: () => notificationService.list(params),
  });
}

export function useUnreadNotificationCount() {
  return useQuery({
    queryKey: QUERY_KEYS.NOTIFICATION_UNREAD_COUNT,
    queryFn: () => notificationService.getUnreadCount(),
    refetchInterval: 60_000,
  });
}

export function useMarkNotificationRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: notificationService.markRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.NOTIFICATIONS });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.NOTIFICATION_UNREAD_COUNT });
    },
    onError: (err) => toast.error(getApiErrorMessage(err, 'Failed to mark notification as read')),
  });
}

export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: notificationService.markAllRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.NOTIFICATIONS });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.NOTIFICATION_UNREAD_COUNT });
      toast.success('All notifications marked as read');
    },
    onError: (err) => toast.error(getApiErrorMessage(err, 'Failed to mark all as read')),
  });
}

export function useDeleteNotification() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: notificationService.remove,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.NOTIFICATIONS });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.NOTIFICATION_UNREAD_COUNT });
      toast.success('Notification deleted');
    },
    onError: (err) => toast.error(getApiErrorMessage(err, 'Failed to delete notification')),
  });
}

export function useNotificationSocket() {
  const { user } = useAuth();
  const { socket, isConnected } = useSocket();
  const queryClient = useQueryClient();

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.NOTIFICATIONS });
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.NOTIFICATION_UNREAD_COUNT });
  }, [queryClient]);

  useEffect(() => {
    if (!socket || !isConnected || !user?.id) return undefined;

    const userId = user.id || user._id;
    socket.emit('join-notifications', { userId });

    const onNew = () => invalidate();
    const onUpdate = () => invalidate();
    const onRead = () => invalidate();
    const onDelete = () => invalidate();

    socket.on('notification:new', onNew);
    socket.on('notification:update', onUpdate);
    socket.on('notification:read', onRead);
    socket.on('notification:delete', onDelete);

    return () => {
      socket.emit('leave-notifications', { userId });
      socket.off('notification:new', onNew);
      socket.off('notification:update', onUpdate);
      socket.off('notification:read', onRead);
      socket.off('notification:delete', onDelete);
    };
  }, [socket, isConnected, user?.id, user?._id, invalidate]);
}

export default useNotificationSocket;
